// The heads-up display: everything Tamagui owns, laid over the canvas.
//
// §15's split is strict — "WebGL owns the map... Tamagui owns everything else" —
// and this component is the everything-else side of that line. It never touches
// the canvas, never reads the board, and never renders a pixel: it renders one
// immutable snapshot of HudStore and nothing more.
//
// The layout is the film's: instruments down the left, the report down the right,
// tick-marked rails along the edges, and the whole middle left empty because the
// middle is the war.

import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { Stack, XStack, YStack } from 'tamagui';
import { Rail } from './chrome';
import { AudioPanel, BankPanel, FeedPanel, LinkPanel, OutcomePanel, TargetPanel } from './panels';
import { CommentPanel } from './CommentPanel';
import { Reticle, type ReticleBox } from './Reticle';
import type { HudStore } from './hudState';
import type { Bus } from '../audio/mixer';

export interface HudProps {
  store: HudStore;
  reticle: ReticleBox | null;
  onVolume: (bus: Bus, v: number) => void;
  onReducedMotion: (reduced: boolean) => void;
}

export function Hud({ store, reticle, onVolume, onReducedMotion }: HudProps): ReactNode {
  const snap = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
  return (
    <Stack position="absolute" top={0} left={0} right={0} bottom={0} pointerEvents="box-none">
      <Reticle box={reticle} />

      {/* Tick-marked rails along the map edges. */}
      <Stack position="absolute" top={0} left={0} right={0} height={8} paddingHorizontal="$md">
        <Rail count={40} />
      </Stack>
      <Stack position="absolute" bottom={0} left={0} right={0} height={8} paddingHorizontal="$md">
        <Rail count={40} />
      </Stack>

      <XStack flex={1} padding="$md" gap="$md" pointerEvents="box-none" alignItems="flex-start">
        <YStack gap="$sm" pointerEvents="auto">
          <BankPanel snap={snap} />
          <OutcomePanel snap={snap} />
          <TargetPanel snap={snap} />
          <CommentPanel pixel={snap.hover?.pixel ?? null} />
        </YStack>

        <Stack flex={1} pointerEvents="none" />

        <YStack gap="$sm" pointerEvents="auto">
          <LinkPanel snap={snap} />
          <FeedPanel feed={snap.feed} />
          <AudioPanel snap={snap} onVolume={onVolume} onReducedMotion={onReducedMotion} />
        </YStack>
      </XStack>
    </Stack>
  );
}
